import React, { useState, useEffect } from 'react';
import { Drawer, Stack, Button, Group, TextInput, MultiSelect, Select, NumberInput, Text, Alert, Divider } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

interface SettingsPanelProps {
  opened: boolean;
  onClose: () => void;
  dashboardName: string;
  dashboardType: 'business' | 'marketing';
  isDefaultDashboard?: boolean;
  selectedCampaigns: string[];
  primaryGoal: string;
  revenueGoal: string | null;
  campaignReport: string;
  costThreshold: number;
  varianceThreshold?: number;
  projectionDefault?: number;
  conversionRateBenchmark?: number | null;
  costPerGoalBenchmark?: number | null;
  // Options
  campaignOptions: Array<{ value: string; label: string }>;
  goalOptions: Array<{ value: string; label: string }>;
  revenueGoalOptions: Array<{ value: string; label: string }>;
  campaignReportOptions: Array<{ value: string; label: string }>;
  // Callbacks
  onSave: (config: SettingsPanelConfig) => Promise<void>;
  /**
   * Called whenever the user changes the Campaigns multi-select. Parent must
   * re-fetch the goal + revenue-goal option lists (intersection across the
   * new campaign set). Supplying this prop enables the intersection-filter UX.
   */
  onCampaignsChange?: (campaignIds: string[]) => void | Promise<void>;
  isLoading?: boolean;
}

export interface SettingsPanelConfig {
  dashboard_name: string;
  campaign_ids: string[];
  goal_id: string;
  revenue_goal_id: string | null;
  campaign_report_id: string;
  cost_threshold: number;
  variance_threshold?: number;
  projection_default?: number;
  conversion_rate_benchmark?: number | null;
  cost_per_goal_benchmark?: number | null;
  set_as_default: boolean;
}


export function SettingsPanel({
  opened,
  onClose,
  dashboardName,
  dashboardType,
  isDefaultDashboard = false,
  selectedCampaigns,
  primaryGoal,
  revenueGoal,
  campaignReport,
  costThreshold,
  varianceThreshold = 20,
  projectionDefault,
  conversionRateBenchmark,
  costPerGoalBenchmark,
  campaignOptions,
  goalOptions,
  revenueGoalOptions,
  campaignReportOptions,
  onSave,
  onCampaignsChange,
  isLoading = false,
}: SettingsPanelProps) {
  const [name, setName] = useState(dashboardName);
  const [campaigns, setCampaigns] = useState<string[]>(selectedCampaigns);
  const [goal, setGoal] = useState(primaryGoal);
  const [revenueGoalId, setRevenueGoalId] = useState<string | null>(revenueGoal);
  const [report, setReport] = useState(campaignReport);
  const [threshold, setThreshold] = useState(costThreshold);
  const [variance, setVariance] = useState(varianceThreshold);
  const [projection, setProjection] = useState(projectionDefault || 2.0);
  const [crBenchmark, setCrBenchmark] = useState<number | null>(
    conversionRateBenchmark != null ? conversionRateBenchmark : null
  );
  const [cpaBenchmark, setCpaBenchmark] = useState<number | null>(
    costPerGoalBenchmark != null ? costPerGoalBenchmark : null
  );
  const [setAsDefault, setSetAsDefault] = useState<boolean>(isDefaultDashboard);
  const [error, setError] = useState('');
  const [confirmWarning, setConfirmWarning] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setName(dashboardName);
      setCampaigns(selectedCampaigns);
      setGoal(primaryGoal);
      setRevenueGoalId(revenueGoal || null);
      setReport(campaignReport);
      setThreshold(costThreshold);
      setVariance(varianceThreshold);
      setProjection(projectionDefault || 2.0);
      setCrBenchmark(conversionRateBenchmark != null ? conversionRateBenchmark : null);
      setCpaBenchmark(costPerGoalBenchmark != null ? costPerGoalBenchmark : null);
      setSetAsDefault(isDefaultDashboard);
      setError('');
      setConfirmWarning(null);
    }
  }, [
    opened,
    dashboardName,
    selectedCampaigns,
    primaryGoal,
    revenueGoal,
    campaignReport,
    costThreshold,
    varianceThreshold,
    projectionDefault,
    conversionRateBenchmark,
    costPerGoalBenchmark,
    isDefaultDashboard,
  ]);

  // If the user picks a Primary Goal that is no longer in the intersection
  // (because they just removed a campaign that was its only remaining home),
  // clear the stale selection so the UI reflects reality.
  useEffect(() => {
    if (!opened || goalOptions.length === 0) return;
    if (goal && !goalOptions.some((opt) => opt.value === goal)) {
      setGoal('');
    }
  }, [opened, goalOptions, goal]);

  useEffect(() => {
    if (!opened || revenueGoalOptions.length === 0) return;
    if (revenueGoalId && !revenueGoalOptions.some((opt) => opt.value === revenueGoalId)) {
      setRevenueGoalId(null);
    }
  }, [opened, revenueGoalOptions, revenueGoalId]);

  const hasChanges =
    name !== dashboardName ||
    campaigns.length !== selectedCampaigns.length ||
    !campaigns.every((c) => selectedCampaigns.includes(c)) ||
    goal !== primaryGoal ||
    revenueGoalId !== revenueGoal ||
    report !== campaignReport ||
    threshold !== costThreshold ||
    variance !== varianceThreshold ||
    projection !== (projectionDefault || 2.0) ||
    crBenchmark !== (conversionRateBenchmark != null ? conversionRateBenchmark : null) ||
    cpaBenchmark !== (costPerGoalBenchmark != null ? costPerGoalBenchmark : null) ||
    setAsDefault !== isDefaultDashboard;

  const handleSave = async () => {
    setError('');
    // Do NOT reset confirmWarning here — we need to detect the second click
    // (when confirmWarning is already set) to proceed with the save.

    if (!name.trim()) {
      setError('Dashboard name is required');
      return;
    }
    if (campaigns.length === 0) {
      setError('Select at least one campaign');
      return;
    }
    if (!goal) {
      // When the intersection is empty, the dropdown has no options and the
      // user cannot pick anything — give them a clear reason why.
      if (goalOptions.length === 0 && campaigns.length > 0) {
        setError('These campaigns have no goals in common. Remove a campaign or choose a different set.');
      } else {
        setError('Primary goal is required');
      }
      return;
    }
    if (goalOptions.length > 0 && !goalOptions.some((opt) => opt.value === goal)) {
      setError('The selected Primary Goal is not available in every chosen campaign. Pick a different goal.');
      return;
    }
    if (revenueGoalId && revenueGoalOptions.length > 0 && !revenueGoalOptions.some((opt) => opt.value === revenueGoalId)) {
      setError('The selected Revenue Goal is not available in every chosen campaign. Pick a different revenue goal or clear it.');
      return;
    }

    // First click on Save: if any destructive change is pending, render a
    // warning directly above the Save button and wait for a second click.
    // Second click (confirmWarning already set): fall through and save.
    if (!confirmWarning) {
      const warnings: string[] = [];
      if (goal !== primaryGoal) {
        warnings.push('Changing the Primary Goal will update all metrics, labels, and benchmarks throughout this dashboard.');
      }
      if (revenueGoalId === null && revenueGoal !== null) {
        warnings.push('Removing the Revenue Goal will clear all revenue metrics and disable profitability calculations.');
      }
      if (report !== campaignReport && report !== '') {
        warnings.push('Changing the Campaign Report will update all benchmarks throughout this dashboard.');
      }
      if (warnings.length > 0) {
        setConfirmWarning(warnings.join('\n\n'));
        return;
      }
    }

    try {
      await onSave({
        dashboard_name: name.trim(),
        campaign_ids: campaigns,
        goal_id: goal,
        revenue_goal_id: revenueGoalId,
        campaign_report_id: report,
        cost_threshold: threshold,
        variance_threshold: variance,
        projection_default: projection,
        conversion_rate_benchmark: crBenchmark,
        cost_per_goal_benchmark: cpaBenchmark,
        set_as_default: setAsDefault,
      });
      setConfirmWarning(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title="Dashboard Settings"
      padding="xl"
      styles={{
        title: {
          WebkitTapHighlightColor: 'transparent',
          color: 'inherit',
          textDecoration: 'none',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
          fontWeight: 700,
          fontSize: '1.125rem',
          lineHeight: 1.45,
          margin: 0,
        },
      }}
    >
      <Stack spacing="xl">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}

        {/* Section 1: Dashboard Configuration */}
        <Stack spacing="md">
          <TextInput
            label="Dashboard Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={60}
          />

          <Select
            label="Default Dashboard"
            data={[
              { value: '1', label: 'Make this dashboard default' },
              { value: '0', label: 'Keep current default dashboard' },
            ]}
            value={setAsDefault ? '1' : '0'}
            onChange={(value) => setSetAsDefault(value === '1')}
          />

          <MultiSelect
            label="Campaigns"
            placeholder="Select campaigns"
            data={campaignOptions}
            value={campaigns}
            onChange={(next) => {
              setCampaigns(next);
              // Trigger the parent to re-fetch goal options for the new
              // campaign set — drives the intersection-filter UX.
              if (onCampaignsChange) onCampaignsChange(next);
            }}
            searchable
            withinPortal
            dropdownPosition="bottom"
          />

          <Select
            label="Primary Goal"
            placeholder={goalOptions.length === 0 && campaigns.length > 0 ? 'No goals in common across selected campaigns' : 'Select goal'}
            description={goalOptions.length === 0 && campaigns.length > 0 ? 'Remove a campaign or pick a different set.' : undefined}
            data={goalOptions}
            value={goal}
            onChange={(value) => setGoal(value || '')}
            disabled={campaigns.length === 0 || goalOptions.length === 0}
            searchable
            withinPortal
            dropdownPosition="bottom"
          />

          <Select
            label="Revenue Goal"
            placeholder={revenueGoalOptions.length === 0 ? 'No revenue goals available' : 'Select revenue goal (optional)'}
            data={revenueGoalOptions}
            value={revenueGoalId || ''}
            onChange={(value) => setRevenueGoalId(value || null)}
            disabled={revenueGoalOptions.length === 0}
            searchable
            clearable
            withinPortal
            dropdownPosition="bottom"
          />

          <Select
            label="Campaign Report"
            placeholder="Select report"
            data={campaignReportOptions}
            value={report}
            onChange={(value) => setReport(value || '')}
            searchable
            clearable
            withinPortal
            dropdownPosition="bottom"
          />

        </Stack>

        <Divider />

        {/* Section 2: Display Thresholds */}
        <Stack spacing="md">
          <Text fw={600} size="sm" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Display Thresholds
          </Text>

          {dashboardType === 'business' && (
            <NumberInput
              label="Cost per Goal Benchmark ($)"
              description="Target cost-per-conversion for the Primary Goal. Used to colour the Cost per Goal card and Traffic Sources status. Leave blank to fall back to the attached Campaign Report's CPA benchmark."
              value={cpaBenchmark ?? ''}
              onChange={(value) => setCpaBenchmark(typeof value === 'number' ? value : null)}
              min={0}
              step={0.01}
              precision={2}
              icon={<span>$</span>}
            />
          )}

          <NumberInput
            label="Cost Benchmark Threshold (%)"
            description="The % above target that triggers a cost warning"
            value={threshold}
            onChange={(value) => setThreshold(value || 20)}
            min={5}
            max={50}
            rightSection={<span>%</span>}
          />
          <Text size="xs" color="dimmed">
            Example: At {threshold}%, costs up to $600 show amber, above $600 show red (target: $500)
          </Text>

          {dashboardType === 'marketing' && (
            <>
              <NumberInput
                label="Traffic Source Variance Threshold (%)"
                description="The minimum conversion rate difference between sources needed to flag a traffic opportunity"
                value={variance}
                onChange={(value) => setVariance(value || 20)}
                min={5}
                max={50}
                rightSection={<span>%</span>}
              />
              <Text size="xs" color="dimmed">
                Example: At {variance}%, if your best source converts at 1.44% and worst at 0.82%, the 43% variance exceeds the threshold.
              </Text>

              <NumberInput
                label="Projection Default Target (%)"
                description="The default target rate shown in the Impact section when no benchmark is set"
                value={projection}
                onChange={(value) => setProjection(value || 2.0)}
                min={0.01}
                step={0.1}
                rightSection={<span>%</span>}
              />

              <NumberInput
                label="Primary Goal Conversion Rate Benchmark (%)"
                description="Target progression rate for the Primary Goal step. Used by What's Causing the Drop-Off when no Campaign Report benchmark is set. Leave blank to fall back to the attached Campaign Report."
                value={crBenchmark ?? ''}
                onChange={(value) => setCrBenchmark(typeof value === 'number' ? value : null)}
                min={0}
                max={100}
                step={0.1}
                precision={2}
                rightSection={<span>%</span>}
              />
            </>
          )}
        </Stack>

        <Divider />

        {/* Inline confirmation warning — rendered directly above the Save button
            so the user can read the warning and confirm in one place. */}
        {confirmWarning && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{confirmWarning}</Text>
          </Alert>
        )}

        {/* Footer */}
        <Group position="right" spacing="sm">
          <Button variant="default" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            color={confirmWarning ? 'yellow' : 'blue'}
            onClick={handleSave}
            loading={isLoading}
            disabled={!hasChanges && !confirmWarning}
            styles={(theme) => ({
              root: {
                color: confirmWarning ? '#000' : '#fff',
                '&:disabled': {
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : undefined,
                  color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : undefined,
                },
              },
            })}
          >
            {confirmWarning ? 'Confirm & Save' : 'Save Changes'}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
